import * as React from 'react'
import styled from 'styled-components'
import InfiniteScroll from 'react-infinite-scroller'
import { LeaderboardTime } from '.'
import { useInfiniteLoadIndexer } from '../../hooks/infiniteLoadIndexer'
import { VIDEO_LEADERBOARD_QUERY } from '../../graphql/graphql-queries'
import { SmallSpinnerContainer, SmallSpinner } from '../spinner'
import { Mint, OrderBy, Query_Root, VideoLeaderboard } from '../../graphql/indexer-graphql-types'
import { fbEnrichUsersFromIds, fbEnrichVideosFromIds } from '../../data/FirebaseEnrich'
import { VideoFullData } from '../../interfaces'
import { getMarketplaceData } from '../../data/IndexerUtils'
import VideoHeader from './headers/VideoHeader'
import VideoRow from './rows/VideoRow'
import VideoSkeletonItem from './rows/VideoSkeletonItem'
import { mintComparer } from '../../utils/comparer'

const itemsContainerStyle = `
  display: flex;
  flex-wrap: wrap;
  position: relative;
  z-index: 0;
  width: 100%;

  // large desktop
  @media (min-width: 82em) {
    max-width: 1800px;
  }
`

const StyledSkeletonContainer = styled.div`
  ${itemsContainerStyle}
`

const StyledInfiniteScroll = styled(InfiniteScroll)`
  ${itemsContainerStyle}
`

const StyledTable = styled.table`
  width: 100%;
  border: none;
  border-collapse: separate;
  border-spacing: 0 15px;
  // large desktop
  @media (min-width: 82em) {
    max-width: 1800px;
  }
`

interface Props {
  time: LeaderboardTime
  currentIndex: number
  index: number
  updateHeight: () => void
}

const parseItems = (data: Query_Root | undefined): VideoLeaderboard[] => {
  if (!data) {
    return []
  }
  return data.videoLeaderboard?.filter(Boolean)?.filter(({ mintObject }) => !!mintObject) as
    | VideoLeaderboard[]
    | []
}

const enrichItems = async (leaderboardItems: VideoLeaderboard[]): Promise<VideoFullData[]> => {
  const mints = leaderboardItems.map(({ mintObject }) => mintObject) as Mint[] | []
  if (!mints.length) {
    return []
  }

  const ids = mints.map((mint) => mint.id)
  const enrichedVideos = await fbEnrichVideosFromIds(ids)
  const creators = await fbEnrichUsersFromIds(enrichedVideos.map((video) => video.created_by))
  return enrichedVideos.map((video) => {
    const mint = mints.find((mint) => mint.id === video.address)
    const creator = creators.find((creator) => creator.address === video.created_by)
    const leaderboardData = leaderboardItems.find((data) => data.mint === video.address)
    const marketplaceData = mint ? getMarketplaceData(mint) : undefined

    return {
      video,
      creator,
      leaderboardInfo: leaderboardData
        ? {
            sevenDays: leaderboardData.sevenDays,
            thirtyDays: leaderboardData.thirtyDays,
            allTime: leaderboardData.allTime,
            sevenDayPrim: leaderboardData.sevenDayPrim,
            thirtyDayPrim: leaderboardData.thirtyDayPrim,
            allTimePrim: leaderboardData.allPrim,
            sevenDaySec: leaderboardData.sevenDaySec,
            thirtyDaySec: leaderboardData.thirtyDaySec,
            allTimeSec: leaderboardData.allSec,
          }
        : undefined,
      ...marketplaceData,
    }
  })
}

export enum VideoSelectedFilter {
  PRIMARY,
  SECONDARY,
  TOTAL,
}

const TrendingVideos = ({ currentIndex, index, updateHeight, time }: Props): JSX.Element => {
  const [selected, setSelected] = React.useState<VideoSelectedFilter>(VideoSelectedFilter.TOTAL)

  const {
    rawData: originalItems,
    data: items,
    fetchNext,
    isLoading,
    hasNext,
    refetch,
  } = useInfiniteLoadIndexer<VideoFullData, Mint>({
    id: '',
    query: VIDEO_LEADERBOARD_QUERY,
    enrichItems,
    parseItems,
    comparer: mintComparer,
    page_size: 10,
    refetchMarker: 'trendingCollectors',
    variables: {
      orderBy:
        time === LeaderboardTime.ALL
          ? selected === VideoSelectedFilter.TOTAL
            ? [{ allTime: OrderBy.Desc }]
            : selected === VideoSelectedFilter.SECONDARY
            ? [{ allSec: OrderBy.Desc }]
            : [{ allPrim: OrderBy.Desc }]
          : time === LeaderboardTime.MONTH
          ? selected === VideoSelectedFilter.TOTAL
            ? [{ thirtyDays: OrderBy.Desc }]
            : selected === VideoSelectedFilter.SECONDARY
            ? [{ thirtyDaySec: OrderBy.Desc }]
            : [{ thirtyDayPrim: OrderBy.Desc }]
          : selected === VideoSelectedFilter.TOTAL
          ? [{ sevenDays: OrderBy.Desc }]
          : selected === VideoSelectedFilter.SECONDARY
          ? [{ sevenDaySec: OrderBy.Desc }]
          : [{ sevenDayPrim: OrderBy.Desc }],
    },
  })

  React.useEffect(() => {
    if (index === currentIndex) {
      refetch()
    }
  }, [currentIndex, index, time, selected])

  React.useEffect(() => {
    if (index === currentIndex && !isLoading) {
      updateHeight()
    }
  }, [items])

  if ((originalItems.length === 0 && hasNext) || isLoading) {
    return (
      <StyledSkeletonContainer>
        <StyledTable>
          <VideoHeader selected={selected} setSelected={setSelected} />
          <tbody>
            {[...Array(10).keys()].map((index) => (
              <VideoSkeletonItem rank={index + 1} key={`skeleton-item-${index}`} />
            ))}
          </tbody>
        </StyledTable>
      </StyledSkeletonContainer>
    )
  }

  if (items.length === 0) {
    return <></>
  }

  return (
    <StyledInfiniteScroll
      loadMore={fetchNext}
      hasMore={hasNext}
      loader={
        <SmallSpinnerContainer>
          <SmallSpinner />
        </SmallSpinnerContainer>
      }
      useWindow
    >
      <StyledTable>
        <VideoHeader selected={selected} setSelected={setSelected} />
        <tbody>
          {items.map(
            (item: VideoFullData, index: number): JSX.Element => (
              <VideoRow
                time={time}
                filter={selected}
                rank={index + 1}
                {...item}
                key={`list-item-${index}`}
              />
            )
          )}
        </tbody>
      </StyledTable>
    </StyledInfiniteScroll>
  )
}

export default TrendingVideos
